import * as React from 'react'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'

import * as ds from '@bob/design-system'
import { styles as S } from '@bob/design-system'
import * as entity from '@boutique/entities'

import { BrushTitleAnimated } from '@bob/components'

import { Appear } from './Appear'
import { CarouselTextsStatic } from './CarouselTextsStatic'
import { CriteriaSlider } from './CriteriaSlider'

export type OurCriteriaProps = {
  manifesto: entity.manifesto.Manifesto
} & React.HTMLAttributes<HTMLDivElement>

function OurCriteriaComponent({ manifesto, ...rest }: OurCriteriaProps): React.ReactElement {
  const [activeCarousel, setActiveCarousel] = React.useState<number>()
  const [activeSocialCarousel, setActiveSocialCarousel] = React.useState<number>()
  const inViewOptions = {
    threshold: 0.0,
    rootMargin: `0px 0px -40% 0px`,
    initialInView: false,
    triggerOnce: true,
  }

  const [$body, inViewBody] = useInView(inViewOptions)
  const [$footer, inViewFooter] = useInView(inViewOptions)

  const { pillars } = manifesto

  const environmentalCriteria = React.useMemo(
    () => [
      ...Object.entries(pillars)
        .filter(([type]) => type === 'environmental')
        .flatMap(([, criteria]) => criteria?.map((criterion) => criterion)),
    ],
    [pillars],
  )
  const socialCriteria = React.useMemo(
    () => [
      ...Object.entries(pillars)
        .filter(([type]) => type === 'social')
        .flatMap(([, criteria]) => criteria?.map((criterion) => criterion)),
    ],
    [pillars],
  )

  return (
    <OurCriteriaSection {...rest}>
      <Body ref={$body}>
        <BrushTitleAnimated
          tag="h3"
          Component={S.heading.Title2}
          text="Pour une planète plus nette"
          inView={inViewBody}
        />
        <CriteriaSlider
          activeIndex={activeCarousel}
          setActiveIndex={setActiveCarousel}
          items={environmentalCriteria}
          backgroundColor={'YELLOW'}
        />
        <CarouselTextsStatic items={environmentalCriteria} activeIndex={activeCarousel} />
        <CarouselSchemas>
          <ds.stickers.others.UsefulKnowledge />
          {environmentalCriteria.map(({ manifesto }, index) => (
            <ds.LazyImage key={manifesto.imageContent.src}>
              <CarouselSchema
                isActive={index === activeCarousel}
                src={manifesto.imageContent.src}
                alt={manifesto.imageContent.alt ?? ''}
              />
            </ds.LazyImage>
          ))}
        </CarouselSchemas>
      </Body>
      <Footer ref={$footer}>
        <BrushTitleAnimated
          tag="h3"
          Component={S.heading.Title2}
          text="Pour le bien des humains"
          inView={inViewFooter}
        />

        <CriteriaSlider
          activeIndex={activeSocialCarousel}
          setActiveIndex={setActiveSocialCarousel}
          items={socialCriteria}
          backgroundColor={'YELLOW'}
        />
        <CarouselSocialTextsStatic items={socialCriteria} activeIndex={activeSocialCarousel} />
      </Footer>
    </OurCriteriaSection>
  )
}

export const OurCriteria = styled(OurCriteriaComponent)``

const CarouselSocialTextsStatic = styled(CarouselTextsStatic)``

const OurCriteriaSection = styled.section`
  background: ${ds.color.YELLOW};
  padding-top: 66px;
  padding-bottom: 60px;

  @media screen and (${ds.breakpoint.LAPTOP}) {
    padding-bottom: 96px;
  }
`

const ContainerGrid = styled(S.container.Base)`
  display: grid;
  grid-column-gap: 20px;

  @media screen and (${ds.breakpoint.LAPTOP}) {
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: 32px;
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    grid-column-gap: 36px;
  }
`

const Body = styled(ContainerGrid)`
  padding-bottom: 115px;

  ${S.heading.Title2} {
    grid-column: 1 / -1;
    text-align: center;
  }

  & > ${CriteriaSlider} {
    grid-column: 1 / -1;
    margin-bottom: 35px;
  }

  & > ${CarouselTextsStatic} {
    grid-column: 1 / -1;
    margin-bottom: 50px;
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    padding-bottom: 175px;

    ${S.heading.Title2} {
      margin-bottom: 73px;
    }

    & > ${CarouselTextsStatic} {
      grid-column: auto / span 6;
      margin-bottom: 0;
    }

    & > ${CriteriaSlider} {
      margin-bottom: 50px;
    }
  }
`

const CarouselSchemas = styled.div`
  position: relative;
  grid-column: 1 / -1;

  ${ds.stickers.Sticker} {
    position: absolute;
    top: -73px;
    right: -20px;
    width: 110px;
    height: 110px;
    z-index: 3;
    background: ${ds.color.PINK};
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    ${ds.stickers.Sticker} {
      width: 160px;
      height: 160px;
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    grid-column: auto / span 6;

    ${ds.stickers.Sticker} {
      width: 177px;
      height: 177px;
    }
  }
`

const CarouselSchema = styled.img<{ isActive?: boolean }>`
  position: ${({ isActive }) => (isActive ? 'relative' : 'absolute')};
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  transition: opacity 0.2s;
  width: 100%;
`

const Footer = styled(ContainerGrid)`
  ${S.heading.Title2} {
    text-align: center;
  }

  & > ${CriteriaSlider} {
    grid-column: 1 / -1;
    margin-bottom: 35px;
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    ${S.heading.Title2} {
      grid-column: 1 / -1;
      margin-bottom: 73px;
    }

    & > ${CriteriaSlider} {
      margin-bottom: 50px;
    }

    & > ${CarouselSocialTextsStatic} {
      grid-column: 4 / span 6;
    }
  }
`

const FooterActions = styled(Appear)`
  text-align: center;

  ${S.heading.TitleCardArticle} {
    margin: 35px 0;

    a {
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    grid-column: 4 / span 6;

    ${S.heading.TitleCardArticle} {
      margin: 55px 0;
    }
  }
`
